/**
 * Renders a chat chart's <svg> to a raster PNG and either downloads it or copies it to
 * the clipboard -- no external dependency (matching the table's hand-rolled CSV export).
 *
 * The chart plot is SVG; the title and legend are HTML rendered around it, so they're
 * composited onto the canvas here rather than captured from the DOM -- that keeps the
 * export self-contained and avoids the canvas tainting that HTML-in-<foreignObject>
 * rasterization hits. The SVG is serialized and drawn via a Blob-URL <img> (same-origin,
 * so the canvas stays untainted and `toBlob` works). Output is scaled for a crisp image.
 */

import {AI_PROVENANCE_TEXT, withPngTextChunks} from './pngTextChunks'

/** Pixel-density multiplier for the exported raster. */
const DEFAULT_SCALE = 3

export interface ChartLegendEntry {
  label: string
  color: string
}

export interface ChartRenderOptions {
  svg: SVGSVGElement
  /** Solid background for the PNG (charts are otherwise transparent). */
  background: string
  titleColor: string
  legendColor: string
  fontFamily: string
  title?: string
  /** Drawn above the plot. Omit for charts that already render their legend in the SVG (pie). */
  legend?: ChartLegendEntry[]
  scale?: number
}

export interface ExportChartPngOptions extends ChartRenderOptions {
  filename: string
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/** Filename-safe slug, umlaut-aware (mirrors the table export's slugify). */
export function slugifyChartName(input: string): string {
  return input
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae')
    .replace(/Ö/g, 'Oe')
    .replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9._-]/g, '')
    .replace(/_+/g, '_')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120)
}

/** Composite the chart (background + title + legend + rasterized plot) into a PNG blob. */
export async function renderChartPngBlob(opts: ChartRenderOptions): Promise<Blob> {
  const {svg, background, titleColor, legendColor, fontFamily, title, legend} = opts
  const scale = opts.scale ?? DEFAULT_SCALE

  // Plot size from the viewBox (the chart is ratio-locked, so this is size-independent).
  const vb = svg.viewBox.baseVal
  const vbW = vb?.width || svg.clientWidth || 640
  const vbH = vb?.height || svg.clientHeight || 300
  const plotW = Math.round(vbW * scale)
  const plotH = Math.round(vbH * scale)

  const pad = Math.round(16 * scale)
  const gap = Math.round(8 * scale)
  const titlePx = Math.round(14 * scale)
  const legendPx = Math.round(11 * scale)
  const rowH = Math.round(16 * scale)
  const swatch = Math.round(10 * scale)
  const swatchGap = Math.round(4 * scale)
  const chipGap = Math.round(14 * scale)

  const canvasW = plotW + pad * 2
  const contentW = canvasW - pad * 2

  const canvas = document.createElement('canvas')
  canvas.width = canvasW
  canvas.height = 10 // provisional; height depends on legend wrapping, measured below
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2D canvas context unavailable')

  const titleH = title ? titlePx + gap : 0

  // Measure the legend chips and flow them into rows within the content width.
  const chips = (legend ?? []).map((e) => {
    ctx.font = `${legendPx}px ${fontFamily}`
    return {label: e.label, color: e.color, w: swatch + swatchGap + ctx.measureText(e.label).width}
  })
  let legendH = 0
  if (chips.length) {
    let x = 0
    let rows = 1
    for (const c of chips) {
      if (x + c.w > contentW && x > 0) {
        rows += 1
        x = 0
      }
      x += c.w + chipGap
    }
    legendH = rows * rowH + gap
  }

  const canvasH = pad * 2 + titleH + legendH + plotH
  canvas.height = canvasH // resizing the canvas resets the 2D context, so paint after this

  // Serialize + rasterize the SVG through a same-origin Blob URL (keeps the canvas
  // untainted). `font-family` is set on the clone because a detached SVG doesn't inherit
  // the page font, so its <text> would otherwise fall back to a default serif.
  const clone = svg.cloneNode(true) as SVGSVGElement
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('width', String(plotW))
  clone.setAttribute('height', String(plotH))
  // The live <svg> has inline width/height:100% (fills its panel). As a detached image
  // that has no container, so clear it and let the width/height attributes above govern
  // the rasterized size deterministically.
  clone.style.width = ''
  clone.style.height = ''
  clone.style.fontFamily = fontFamily
  const svgStr = new XMLSerializer().serializeToString(clone)
  const svgUrl = URL.createObjectURL(new Blob([svgStr], {type: 'image/svg+xml;charset=utf-8'}))

  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = (): void => resolve()
      img.onerror = (): void => reject(new Error('chart SVG rasterization failed'))
      img.src = svgUrl
    })

    ctx.fillStyle = background
    ctx.fillRect(0, 0, canvasW, canvasH)
    ctx.textBaseline = 'top'

    let y = pad
    if (title) {
      ctx.font = `600 ${titlePx}px ${fontFamily}`
      ctx.fillStyle = titleColor
      ctx.fillText(title, pad, y)
      y += titleH
    }
    if (chips.length) {
      ctx.font = `${legendPx}px ${fontFamily}`
      let x = pad
      let lineY = y
      const swatchOffset = Math.round((legendPx - swatch) / 2)
      for (const c of chips) {
        if (x + c.w > pad + contentW && x > pad) {
          x = pad
          lineY += rowH
        }
        ctx.fillStyle = c.color
        ctx.fillRect(x, lineY + swatchOffset, swatch, swatch)
        ctx.fillStyle = legendColor
        ctx.fillText(c.label, x + swatch + swatchGap, lineY)
        x += c.w + chipGap
      }
      y += legendH
    }
    ctx.drawImage(img, pad, y, plotW, plotH)
  } finally {
    URL.revokeObjectURL(svgUrl)
  }

  const png = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('chart PNG encoding failed'))
    }, 'image/png')
  })

  // Mark the bytes as AI-generated before anyone can get hold of them. Both exits --
  // the file download and the clipboard copy -- come through this function, so this is
  // the single place that has to remember to do it.
  return await withPngTextChunks(png, AI_PROVENANCE_TEXT)
}

/** Render the chart to PNG and trigger a file download. */
export async function downloadChartPng(opts: ExportChartPngOptions): Promise<void> {
  const blob = await renderChartPngBlob(opts)
  downloadBlob(blob, opts.filename)
}

/** Whether the browser can copy an image to the clipboard (async ClipboardItem write). */
export function isChartClipboardCopySupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.clipboard &&
    typeof navigator.clipboard.write === 'function' &&
    typeof ClipboardItem !== 'undefined'
  )
}

/** Copy the chart PNG to the clipboard. */
export function copyChartPng(opts: ChartRenderOptions): Promise<void> {
  // Build the ClipboardItem synchronously with a Promise<Blob> so Safari keeps the user
  // gesture -- it rejects a clipboard write issued after an awaited async gap.
  return navigator.clipboard.write([new ClipboardItem({'image/png': renderChartPngBlob(opts)})])
}
